import * as AccessControl from '../../store/mfsAccessControl';
import {
  TreeView,
  Change,
  TraitLabel,
  StableRange,
  TraitLocation,
  Detach,
  StablePlace,
  comparePayloads,
  TreeViewNode,
  SharedTree,
} from '@fluid-experimental/tree';
import {
  getItemNode,
  createMfsPropertyNode,
  createLeafNode,
  NodeKind,
  getUniqueValues,
  PROPERTY_VALUES,
  MfsMultiValueNode,
  getPropertyNode,
  getValue,
} from '.';
import { MfsValue, MfsDeltaValue, add, remove, MfsItemId } from '../..';
import { assertPrecondition, mfsError, Result } from '../../errors';
import { getPropertyKey } from '../../store/mfsAccessControl';

import { isMfsDeltaValue, isMfsValueArray, isMfsSingleValue } from '../../store/mfsValueTypeGuards';
import { hasAnyElement } from '../../utility';
import { findNodesWithPayloads } from './lookup';
import { MfsItemNode, MfsValueNode } from './types';
import { hasAddRemove, getEffectiveValue, getOnlyAddValues } from '../../store/properties';
import { hasTraits, MfsPropertyTrait } from '../../store/mfsProperty';
import { logVerbose } from '../../log';
import { isMultiValuePropertyNode } from './typeGuards';

/** Deletes the given property trait. */
export function deleteMfsItemProperty(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  propertyName: string
): Change | undefined {
  const itemNode = getItemNode(tree, view, itemId);
  const propertyKey = getPropertyKey(propertyName, 'delete') as TraitLabel;
  if (!itemNode.traits.has(propertyKey)) {
    logVerbose(
      'deleteMfsItemProperty',
      `deleteMfsItemProperty found no property with the specified key on itemId: ${itemId}`
    );
    return undefined;
  }
  const propertyTrait = { parent: itemNode.identifier, label: propertyKey };
  return Change.delete(StableRange.all(propertyTrait));
}

export function setMfsItemProperty(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  propertyName: string,
  propertyValue: MfsValue | MfsDeltaValue
): readonly Change[] {
  const itemNode = getItemNode(tree, view, itemId);
  const propertyKey = getPropertyKey(propertyName, 'set') as TraitLabel;
  return setProperty(view, itemNode, propertyKey, propertyValue);
}

export function setProperty(
  view: TreeView,
  itemNode: MfsItemNode,
  propertyKey: TraitLabel,
  propertyValue: MfsValue | MfsDeltaValue
): readonly Change[] {
  const traitLocation: TraitLocation = { parent: itemNode.identifier, label: propertyKey };
  const propertyNode = getPropertyNode(view, itemNode, propertyKey);
  const isNewProperty = propertyNode === undefined;

  // remove existing property
  if (propertyValue === undefined) {
    return isNewProperty ? [] : [deleteProperty(traitLocation)];
  }

  const result = getValueToSet(view, propertyValue, propertyKey, propertyNode);

  if (result.isOK === false) {
    // nothing to change
    logVerbose('setProperty', `Skip setProperty , reason: ${result.error.message}`);
    return [];
  }

  // New property
  if (propertyNode === undefined) {
    return createProperty(result.value, traitLocation);
  }

  // Delta update property
  if (isMfsDeltaValue(result.value)) {
    assertPrecondition(isMultiValuePropertyNode(propertyNode), () => mfsError.invalidPropertyKind(NodeKind.Array));
    return deltaUpdateProperty(view, result.value, propertyNode);
  }

  // Reset property
  if (!areSameValue(view, propertyNode, result.value)) {
    return resetProperty(result.value, traitLocation);
  }

  // no change
  return [];
}

function deleteProperty(trait: TraitLocation): Detach {
  return Change.detach(StableRange.all(trait));
}

function createProperty(propertyValue: MfsValue | MfsDeltaValue, trait: TraitLocation): readonly Change[] {
  const create = (v: MfsValue) => Change.insertTree(createMfsPropertyNode(v), StablePlace.atStartOf(trait));

  // If value provided, create the property.
  if (!isMfsDeltaValue(propertyValue)) {
    return create(propertyValue);
  }
  logVerbose('createProperty', `Creating new property`);
  // If delta provided, create the property using only addValues (since there's nothing to remove from new property)
  return create(propertyValue[add]);
}

/** Resets the value of a given property to the given value. */
function resetProperty(propertyValue: MfsValue, trait: TraitLocation): readonly Change[] {
  return [deleteProperty(trait), ...createProperty(propertyValue, trait)];
}

function areSameValue(view: TreeView, propertyNode: TreeViewNode, value: MfsValue): boolean {
  const currentValue = getValue(view, propertyNode);
  return comparePayloads(currentValue, value);
}

function deltaUpdateProperty(
  view: TreeView,
  deltaValue: MfsDeltaValue,
  propertyNode: MfsMultiValueNode
): readonly Change[] {
  const valuesToAdd = deltaValue[add] ?? [];
  const valuesToRemove = deltaValue[remove] ?? [];
  const changes: Change[] = [];

  if (hasAnyElement(valuesToAdd) && !areSameValue(view, propertyNode, valuesToAdd)) {
    changes.push(
      ...Change.insertTree(
        valuesToAdd.map((v) => createLeafNode(v, NodeKind.MultiValueNode)),
        StablePlace.atEndOf({ parent: propertyNode.identifier, label: PROPERTY_VALUES })
      )
    );
  }

  if (hasAnyElement(valuesToRemove)) {
    // find & remove nodes with matching payloads.
    const valueNodes = propertyNode.traits.get(PROPERTY_VALUES) ?? [];
    const nodeIds = findNodesWithPayloads(view, valuesToRemove, valueNodes);
    changes.push(...nodeIds.map((nodeId) => Change.delete(StableRange.only(nodeId))));
  }

  return changes;
}

/**
 * For both multi-valued & single-valued properties: returns only the values that do not already exist in the store.
 * This also ensures that we don't raise propertyChanged when there's no change.
 */
function getValueToSet(
  view: TreeView,
  propertyValue: MfsValue | MfsDeltaValue,
  propertyKey: TraitLabel,
  propertyNode?: MfsMultiValueNode | MfsValueNode
): Result<MfsValue | MfsDeltaValue> {
  if (isMfsSingleValue(propertyValue) || propertyValue === null || propertyValue === undefined) {
    return Result.ok(propertyValue);
  }

  // preprocess passed in values
  const result = getEffectiveValue(propertyKey, propertyValue);
  if (!result.isOK) return result;

  const effectiveValue = result.value;
  assertPrecondition(effectiveValue !== undefined);
  const { hasAdd, hasRemove } = hasAddRemove(effectiveValue);

  // new property
  if (propertyNode === undefined) {
    return hasAdd
      ? hasRemove
        ? Result.ok(getOnlyAddValues(effectiveValue)) // ignore removed values since the property is new.
        : result
      : Result.fail(new Error(`${propertyKey} is a new property, ignoring removeValues}`));
  }

  if (!hasTraits(propertyKey, MfsPropertyTrait.Unique)) {
    // nothing else to do, if the property is not marked as unique.
    return Result.ok(effectiveValue);
  }

  // otherwise make sure that the effective values are all new values (not already among the values of the property in the current snapshot).
  const valueNodes = propertyNode.traits.get(PROPERTY_VALUES) ?? [];

  if (isMfsValueArray(effectiveValue)) {
    return getUniqueValues(view, effectiveValue, 'put', valueNodes);
  }

  if (isMfsDeltaValue(propertyValue)) {
    if (hasAdd) {
      const valuesToAdd = propertyValue[add];
      assertPrecondition(!!valuesToAdd);
      // exclude the values that already exist in the snapshot.
      const newValuesResult = getUniqueValues(view, valuesToAdd, 'patch', valueNodes);

      return newValuesResult.isOK === true
        ? Result.ok({ ...propertyValue, [add]: newValuesResult.value })
        : propertyValue[remove]
        ? Result.fail(new Error('Nothing to process'))
        : Result.ok({ ...propertyValue, [add]: undefined });
    }
    return Result.ok(effectiveValue);
  }

  // proceed with delta-remove.
  return Result.ok(effectiveValue);
}
